import React, { useEffect, useRef, useState } from 'react';
import customerService from '../../services/customerService';
import CustomerForm from '../customers/CustomerForm';

const CustomerSearchModal = ({ onSelectCustomer, onCancel }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [showCreate]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const customers = await customerService.getCustomers(searchTerm);
      setResults(customers);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-3 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-lg max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold">{showCreate ? 'Add Customer' : 'Find Customer'}</h2>
          {!showCreate && (
            <button
              type="button"
              onClick={() => {
                setShowCreate(true);
                setFormError('');
              }}
              className="px-3 py-2 text-sm font-semibold rounded-full border border-primary text-primary hover:bg-primary hover:text-white"
            >
              Add new customer
            </button>
          )}
        </div>

        {showCreate ? (
          <>
            {formError && <p className="text-sm text-rose-600 mb-2">{formError}</p>}
            <CustomerForm
              customer={null}
              onSave={async (formData, createLoyaltyCard) => {
                try {
                  setFormError('');
                  const payload = { ...formData, createLoyaltyCard };
                  const created = await customerService.createCustomer(payload);
                  onSelectCustomer(created);
                } catch (err) {
                  setFormError(err.response?.data?.message || 'Unable to create customer.');
                }
              }}
              onCancel={() => {
                setShowCreate(false);
                setFormError('');
              }}
            />
          </>
        ) : (
          <>
            <form onSubmit={handleSearch} className="flex space-x-2 mb-4">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full border-gray-300 rounded-md shadow-sm"
                placeholder="Search by name, email, or phone..."
                autoFocus
              />
              <button
                type="submit"
                disabled={isLoading}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark disabled:bg-gray-400"
              >
                {isLoading ? '...' : 'Search'}
              </button>
            </form>
            <div className="h-64 overflow-y-auto border rounded-md">
              {results.map((customer) => (
                <div
                  key={customer._id}
                  onClick={() => onSelectCustomer(customer)}
                  className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                >
                  <p className="font-semibold">{customer.fullName}</p>
                  <p className="text-sm text-gray-600">
                    {customer.email || '—'}
                    {customer.phone ? ` • ${customer.phone}` : ''}
                  </p>
                </div>
              ))}
              {results.length === 0 && !isLoading && (
                <p className="p-4 text-sm text-gray-500">No customers found.</p>
              )}
            </div>
          </>
        )}
        <button onClick={onCancel} className="w-full mt-4 text-center text-gray-600 py-2">Close</button>
      </div>
    </div>
  );
};

export default CustomerSearchModal;
